import type { TableEntry } from '@app/types'

import humps from 'humps'
import type { AdjustmentsData, DetailedEntriesData } from '../types'

export const EMPTY_TABLE_DATA = {
  headers: [] as string[],
  translatedHeaders: [] as string[],
  entries: [] as TableEntry[],
}

// SFetch camelizes entry keys but not header strings, so we align them here.
export function camelizeTableData(data: {
  headers: string[]
  translatedHeaders: string[]
  entries: Record<string, unknown>[]
}): AdjustmentsData {
  return {
    headers: (data.headers || []).map((h) => humps.camelize(h)),
    translatedHeaders: data.translatedHeaders || [],
    entries: data.entries || [],
  }
}

// Prepares detailed entries for the EntriesTable component, which expects
// snake_case headers and entry keys (matching the original Rails format).
// SFetch camelizes entry keys, so we reverse that using the headers list
// as the source of truth. We can't rely on humps.decamelize for this because
// it doesn't round-trip keys with numbers (e.g. analytical_dimension_0
// camelizes to analyticalDimension0 but decamelizes to analytical_dimension0).
export function prepareDetailedEntries(
  data: DetailedEntriesData
): DetailedEntriesData {
  const headers = data.headers || []

  // Map camelized key -> original snake_case header
  const keyMap: Record<string, string> = {}
  for (const header of headers) {
    keyMap[humps.camelize(header)] = header
  }

  return {
    headers,
    translatedHeaders: data.translatedHeaders || [],
    entries: (data.entries || []).map((entry) => {
      const result = {} as TableEntry
      for (const [key, value] of Object.entries(entry)) {
        result[keyMap[key] || humps.decamelize(key)] = value
      }
      return result
    }),
    truncated: data.truncated,
  }
}
